/**
 * Settings → Data: everything this business holds as one ZIP, and deleting the business.
 * Owner only. Deleting warns about § 132 BAO (keeping the Belege is the business's duty for seven
 * years), offers the export first and needs the business name typed.
 */
import { Download, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buildDataExport, dataExportName, deleteRefusal, deleteWorkspace } from "@/lib/api/account";
import { useAuth } from "@/lib/session/auth-context";
import { downloadFile } from "@/lib/export/tables";
import { useWorkspace } from "@/lib/session/workspace-context";

export function DataSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [exported, setExported] = useState(false);

  if (!workspace || !user) return null;

  async function exportData() {
    if (!workspace || !user) return;
    setProgress({ done: 0, total: 0 });
    try {
      const zip = await buildDataExport({
        workspace,
        profile: user,
        readme: t("dataExport.readme", { name: workspace.name }),
        onProgress: (done, total) => setProgress({ done, total }),
      });
      downloadFile(
        dataExportName(workspace.name),
        new Blob([zip as BlobPart], { type: "application/zip" }),
      );
      setExported(true);
      toast.success(t("dataExport.done"));
    } catch (error) {
      toast.error(
        t("dataExport.failed", { message: error instanceof Error ? error.message : String(error) }),
      );
    } finally {
      setProgress(null);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("dataExport.title")}</CardTitle>
          <CardDescription>{t("dataExport.hint")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={() => void exportData()}
            disabled={progress !== null}
            data-testid="export-all-data"
          >
            <Download />
            {progress
              ? t("dataExport.progress", { done: progress.done, total: progress.total })
              : t("dataExport.button")}
          </Button>
        </CardContent>
      </Card>

      <DeleteWorkspaceCard
        workspaceId={workspace.id}
        name={workspace.name}
        exported={exported}
        onExport={() => void exportData()}
      />
    </div>
  );
}

function DeleteWorkspaceCard({
  workspaceId,
  name,
  exported,
  onExport,
}: {
  workspaceId: string;
  name: string;
  exported: boolean;
  onExport: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = typed.trim().toLowerCase() === name.trim().toLowerCase();

  async function remove() {
    setBusy(true);
    try {
      await deleteWorkspace(workspaceId, typed);
      toast.success(t("deleteWorkspace.done", { name }));
      await navigate("/dashboard");
      // Refetches everything on screen, the list of businesses included: the provider then picks
      // the next business or sends the person to create one. (clear() would keep the old answer.)
      await queryClient.resetQueries();
    } catch (error) {
      const reason = deleteRefusal(error);
      toast.error(
        reason === "subscription"
          ? t("deleteWorkspace.subscription")
          : reason === "mismatch"
            ? t("deleteWorkspace.mismatch")
            : t("deleteWorkspace.failed"),
      );
      setBusy(false);
    }
  }

  return (
    <Card className="border-destructive/40" data-testid="delete-workspace">
      <CardHeader>
        <CardTitle>{t("deleteWorkspace.title")}</CardTitle>
        <CardDescription>{t("deleteWorkspace.hint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <TriangleAlert className="size-4" />
          <AlertTitle>{t("deleteWorkspace.retentionTitle")}</AlertTitle>
          <AlertDescription>
            <p>{t("deleteWorkspace.retention")}</p>
            {!exported && (
              <Button variant="link" className="h-auto p-0" onClick={onExport}>
                {t("deleteWorkspace.exportFirst")}
              </Button>
            )}
          </AlertDescription>
        </Alert>
        <div className="space-y-2">
          <Label htmlFor="confirm-workspace-name">
            {t("deleteWorkspace.confirmLabel", { name })}
          </Label>
          <Input
            id="confirm-workspace-name"
            value={typed}
            autoComplete="off"
            onChange={(event) => setTyped(event.target.value)}
            data-testid="confirm-workspace-name"
          />
        </div>
        <Button
          variant="destructive"
          disabled={!matches || busy}
          onClick={() => void remove()}
          data-testid="delete-workspace-button"
        >
          {t("deleteWorkspace.button")}
        </Button>
      </CardContent>
    </Card>
  );
}
